import Axios from 'axios';
import loadPixel from './scripts/fbPixel';
import config from '../config';
import Logger from './Logger';
import { getCookie, isAndroidDevice, isIosDevice, isProdMode } from '../utils';
import { notifyHandledError } from './ErrorMonitoring';

const isProd = isProdMode();
const PASSTHROUGH_EVENTS = [
  'Sign up start',
  'CompleteRegistration',
  'InitiateCheckout',
  'StartTrial',
  'Purchase',
  'custom_complete_registration',
  'custom_initiate_checkout',
  'custom_start_trial',
];
// Meta categorises the dataset from what we send it, so only these custom
// events reach it; every other Analytics event stays off the pixel.
// The custom_ events repeat a standard event under a custom name: Meta blocks
// the standard lower-funnel events on this dataset, but not custom events.
const ALLOWED_CUSTOM_EVENTS = [
  'Sign up start',
  'af_achievement_unlocked',
  'Web Subscription View',
  'Web Subscription Close',
  'Subscription purchase',
  'Get App Action',
  'custom_add_to_cart',
  'custom_complete_registration',
  'custom_initiate_checkout',
  'custom_start_trial',
];

// The pixels every event goes to: the web pixels, plus the device pixel for
// Android or iOS browsers.
function getPixelIds() {
  const ids = [...config.pixel.web];
  if (isAndroidDevice()) {
    ids.push(config.pixel.webAndroid);
  }
  if (isIosDevice()) {
    ids.push(config.pixel.webIos);
  }
  return [...new Set(ids.filter(Boolean))];
}

function init() {
  if (isProd) {
    setTimeout(() => {
      if (typeof fbq === 'undefined') {
        Logger.debug('Loading pixel');
        loadPixel();
      }
      if (!fbq.getState || fbq.getState().pixels.length === 0) {
        Logger.debug('Initalizing pixel');

        getPixelIds().forEach((id) => fbq('init', id));

        trackStandard('PageView');
      }
    }, 1000);
  }
}

function getEventId({ id } = {}) {
  const eventID = id
    ? `${id}.${new Date().getTime()};`
    : `${new Date().getTime()}`;
  return eventID;
}

// Event data is dropped unless includeEventData is set, because
// Analytics.track forwards every Mixpanel event here with its properties.
function trackCustom(
  event,
  data,
  { includeEventData, passThroughData, user } = {}
) {
  if (!ALLOWED_CUSTOM_EVENTS.includes(event)) {
    return;
  }
  if (isProd && typeof fbq !== 'undefined') {
    const eventData = includeEventData ? data : {};
    const eventID = getEventId(user || {});
    Logger.debug('FB tracking Custom', { event, data, eventID });
    getPixelIds().forEach((id) =>
      fbq('trackSingleCustom', id, event, eventData, { eventID })
    );
    trackApiPassthrough(event, eventData, { user, eventID, passThroughData });
  }
}

function trackStandard(event, data, { user, passThroughData } = {}) {
  if (isProd && typeof fbq !== 'undefined') {
    const eventID = getEventId(user || {});
    Logger.debug('FB tracking standard', { event, data, eventID });
    getPixelIds().forEach((id) =>
      fbq('trackSingle', id, event, data, { eventID })
    );
    trackApiPassthrough(event, data, { user, eventID, passThroughData });
  }
}

async function trackApiPassthrough(
  event,
  data,
  { user, eventID, passThroughData = {} }
) {
  try {
    Logger.debug('Initiating sending passthrough event', {
      event,
      data,
      user,
      eventID,
      passThroughData,
    });
    if (!PASSTHROUGH_EVENTS.includes(event)) {
      Logger.debug('Ignoring non pass through event', { event, data });
      return;
    }
    const body = {
      id: eventID,
      ...data,
      ...passThroughData,
      user: {
        pixelCookies: { fbp: getFBP(), fbc: getFBC() },
        ...user,
      },
      name: event,
    };
    if (typeof window !== 'undefined') {
      body.sourceUrl = window.location?.href;
    }
    const options = {
      method: 'POST',
      url: `${config.api.auraServices}/pt/pixel`,
      data: body,
      headers: {
        'Content-Type': 'application/json',
      },
      json: true,
    };
    const response = await Axios(options);
    // if (!response || !response.data) {
    //   notifyHandledError(null, {
    //     message: 'Received null response from FB passthrough event',
    //     event,
    //     data,
    //   });
    // }
    Logger.debug('Sent passthrough FB pixel event', { response });
  } catch (error) {
    notifyHandledError(error, {
      message: 'Failed to send FB passthrough event',
      event,
      data,
    });
  }
}

function getFBC() {
  return getCookie('_fbc');
}

function getFBP() {
  return getCookie('_fbp');
}

const FbPixel = {
  init,
  trackCustom,
  trackStandard,
  getFBC,
  getFBP,
};

export default FbPixel;
